import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { CalendarIcon, ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import Navbar from "../components/lac/Navbar";
import Footer from "../components/lac/Footer";
import { api, formatApiError, SERVICES } from "../lib/api";
import BookingSuccess from "./booking/BookingSuccess";
import CustomPartsPanel from "./booking/CustomPartsPanel";
import StepContact from "./booking/StepContact";

const inputCls = "bg-[#0d0d0d] border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-[#D946EF] focus-visible:border-[#D946EF] h-12 rounded-sm";

// 3-hour booking blocks
const BLOCKS = [
  { id: "morning",   label: "Morning",   range: "9am – 12pm", start: "09:00", api_label: "09:00 – 12:00" },
  { id: "afternoon", label: "Afternoon", range: "12pm – 3pm", start: "12:00", api_label: "12:00 – 15:00" },
  { id: "evening",   label: "Evening",   range: "3pm – 6pm",  start: "15:00", api_label: "15:00 – 18:00" },
];

const slotToBlockRange = (slot) => {
  if (!slot) return "";
  if (["09:00","10:00","11:00"].includes(slot)) return "9am – 12pm";
  if (["12:00","13:00","14:00"].includes(slot)) return "12pm – 3pm";
  if (["15:00","16:00","17:00"].includes(slot)) return "3pm – 6pm";
  return slot;
};

const formatReg = (raw) => {
  const v = raw.toUpperCase().replace(/[^A-Z0-9]/g, "");
  // UK plate: AB12 CDE → split after 4 chars
  if (v.length > 4) return `${v.slice(0, 4)} ${v.slice(4, 7)}`;
  return v;
};

export default function Booking() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialReg = searchParams.get("reg");
  const initialVehicle = searchParams.get("vehicle");
  const initialMake = searchParams.get("make");
  const [step, setStep] = useState(1); // 1 = service+vehicle, 2 = date+time, 3 = contact
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Reg-first gate
  const [reg, setReg] = useState(initialReg ? formatReg(initialReg) : "");
  const [vehicleMake, setVehicleMake] = useState(initialMake || "");
  const initialYear = searchParams.get("year");
  const [vehicleYear, setVehicleYear] = useState(initialYear ? parseInt(initialYear, 10) : null);
  const [regChecking, setRegChecking] = useState(false);
  const [regConfirmed, setRegConfirmed] = useState(!!initialReg); // true once user enters reg (or chose to skip)
  const [regSkipped, setRegSkipped] = useState(false);

  const isMercedes = (vehicleMake || "").toUpperCase().includes("MERCEDES");
  const platePresent = !!reg.trim() && !regSkipped;

  const [service, setService] = useState("");
  const [vehicle, setVehicle] = useState(initialVehicle || (initialReg ? formatReg(initialReg) : ""));
  const [bookingDate, setBookingDate] = useState(null);
  const [timeSlot, setTimeSlot] = useState("");
  const [allSlots, setAllSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [blocksRemaining, setBlocksRemaining] = useState({});
  const [blocksPassed, setBlocksPassed] = useState({});
  const [maxPerBlock, setMaxPerBlock] = useState(2);
  const [starlightRemaining, setStarlightRemaining] = useState(null);
  const [restrictedDay, setRestrictedDay] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const [packages, setPackages] = useState({});
  const [comboInfo, setComboInfo] = useState({ discount_pct: 15, description: "" });
  const [promoInfo, setPromoInfo] = useState({ active: false, discount_pct: 0, label: "", ends_on: "" });
  const [addonsInfo, setAddonsInfo] = useState({
    screen_code: { price: 200, eligible_keywords: [] },
    shooting_stars: { price: 80, eligible_packages: [] },
  });
  const [signaturePremiumKeywords, setSignaturePremiumKeywords] = useState([]);
  const [pkg, setPkg] = useState("");
  const [starlightPkg, setStarlightPkg] = useState("");
  const [ambientPkg, setAmbientPkg] = useState("");
  // Add-on toggles
  const [screenCode, setScreenCode] = useState(false);
  const [shootingStars, setShootingStars] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [agreed, setAgreed] = useState(false);

  // Customer-entered promo code
  const [promoCode, setPromoCode] = useState("");
  const [promoState, setPromoState] = useState({ status: "idle", discount_pct: 0, label: "", error: "" });
  const promoActiveApplied = promoState.status === "valid" && promoState.discount_pct > 0;
  const applyCustomerPromo = (n) => (promoActiveApplied ? Math.round(n * (1 - promoState.discount_pct / 100) * 100) / 100 : n);

  const validatePromo = async () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) return;
    setPromoState({ status: "checking", discount_pct: 0, label: "", error: "" });
    try {
      const r = await api.post("/promo/validate", { code });
      setPromoState({ status: "valid", discount_pct: r.data.discount_pct, label: r.data.label, error: "" });
      toast.success(`${code} applied — ${r.data.discount_pct}% off`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = detail === "promo_expired" ? "That code has expired." : "We didn't recognise that code.";
      setPromoState({ status: "invalid", discount_pct: 0, label: "", error: msg });
      toast.error(msg);
    }
  };
  const clearPromo = () => {
    setPromoCode("");
    setPromoState({ status: "idle", discount_pct: 0, label: "", error: "" });
  };

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    api.get("/packages").then((r) => {
      const data = r.data;
      if (data && data.packages) {
        setPackages(data.packages);
        if (data.combo) setComboInfo(data.combo);
        if (data.promo) setPromoInfo(data.promo);
        if (data.addons) setAddonsInfo(data.addons);
        if (data.eligibility?.signature_premium_keywords) {
          setSignaturePremiumKeywords(data.eligibility.signature_premium_keywords);
        }
      } else {
        setPackages(data || {});
      }
    }).catch(() => {});
  }, []);

  // Reset packages & add-ons when service changes — keep combo selections if switching INTO combo
  useEffect(() => {
    setPkg("");
    setScreenCode(false);
    setShootingStars(false);
    if (service !== "Combo") {
      setStarlightPkg("");
      setAmbientPkg("");
    }
  }, [service]);

  // Strip add-ons that no longer apply when package/vehicle changes
  useEffect(() => { setShootingStars(false); }, [pkg, starlightPkg]);
  useEffect(() => { setScreenCode(false); }, [pkg, ambientPkg, vehicle]);

  const buildVehicleString = (v) =>
    [v.make, v.year, v.colour].filter(Boolean).join(" · ");

  const lookupReg = async (e) => {
    e?.preventDefault?.();
    const compact = reg.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (!compact) return;
    setRegChecking(true);
    try {
      const r = await api.post("/vehicle/lookup", { reg: compact });
      const v = r.data || {};
      setVehicleMake(v.make || "");
      setVehicleYear(v.year ? parseInt(v.year, 10) : null);
      setVehicle(buildVehicleString(v) || formatReg(compact));
      setRegConfirmed(true);
      setRegSkipped(false);
      toast.success(`${v.make || "Vehicle"} matched — we've prepped your options.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "lookup_not_configured" || err?.response?.status === 503) {
        // DVLA disabled — just continue with the raw plate as the vehicle hint
        setVehicleMake("");
        setVehicleYear(null);
        setVehicle(formatReg(compact));
        setRegConfirmed(true);
        toast.message("Looking your reg up — you'll add make/model next.");
      } else if (err?.response?.status === 404) {
        toast.error("We couldn't find that reg on DVLA. Double-check it, or skip and type your car below.");
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setRegChecking(false);
    }
  };

  const skipReg = () => {
    setRegSkipped(true);
    setRegConfirmed(true);
    setVehicleMake("");
    setVehicleYear(null);
  };

  useEffect(() => {
    if (!bookingDate) return;
    const dateStr = format(bookingDate, "yyyy-MM-dd");
    setLoadingSlots(true);
    api.get(`/bookings/availability?date=${dateStr}`)
      .then((r) => {
        setAllSlots(r.data.all_slots);
        setBookedSlots(r.data.booked_slots);
        setBlocksRemaining(r.data.blocks_remaining || {});
        setMaxPerBlock(r.data.max_per_block ?? 2);
        setStarlightRemaining(
          r.data.starlight_remaining === undefined ? null : r.data.starlight_remaining,
        );
        setRestrictedDay(!!r.data.restricted);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoadingSlots(false));
  }, [bookingDate]);

  const servicePackages = (service && packages[service]) || [];
  const selectedPkg = servicePackages.find((p) => p.name === pkg);

  // Vehicle-based eligibility (case-insensitive substring match against the vehicle field)
  const vehicleLower = (vehicle || "").toLowerCase();
  const matchesAny = (kws) => Array.isArray(kws) && kws.some((k) => vehicleLower.includes(k));
  const eligibleForSignaturePremium = matchesAny(signaturePremiumKeywords);
  const eligibleForScreenCode = matchesAny(addonsInfo.screen_code?.eligible_keywords || []);

  // Hide Mercedes-only packages when a non-Mercedes plate was entered.
  // When no plate was entered at all, show everything (current behavior).
  const isMercedesOnly = (p) => /mercedes/i.test(p.name) || /mercedes/i.test(p.tier || "") || p.mercedes_only === true;
  const isSignaturePremium = (p) => p.name === "Signature Premium Package";
  const isMercedesScreenCodedFactory = (p) => p.name === "Mercedes Screen Coded Factory Lighting";

  // Screen-Coded Factory Lighting eligibility:
  //  - All Mercedes: must be 2015+
  //  - Mercedes B-Class: must be 2018+
  //  - If no year is known (manual entry with no year), don't block — the fitter can confirm on the day.
  const parseYearFromString = (s) => {
    const m = (s || "").match(/\b(19|20)\d{2}\b/);
    return m ? parseInt(m[0], 10) : null;
  };
  const effectiveYear = vehicleYear || parseYearFromString(vehicle);
  const looksLikeBClass = /\bb[\s-]?class\b/i.test(vehicle || "");
  const screenCodedFactoryEligible = () => {
    if (!effectiveYear) return true;
    if (looksLikeBClass) return effectiveYear >= 2018;
    return effectiveYear >= 2015;
  };

  const filterForVehicle = (list) =>
    list.filter((p) => {
      // Signature Premium is hidden unless the vehicle matches the eligible Mercedes list
      if (isSignaturePremium(p)) {
        // If no plate was entered AND no vehicle typed, hide it (no way to tell eligibility)
        if (!platePresent && !vehicle.trim()) return false;
        return eligibleForSignaturePremium || isMercedes;
      }
      // Screen-Coded Factory Lighting: apply year + model rules on top of the Mercedes-only filter
      if (isMercedesScreenCodedFactory(p)) {
        // First: must be Mercedes (either via plate or hidden when non-Mercedes plate present)
        if (platePresent && !isMercedes) return false;
        return screenCodedFactoryEligible();
      }
      // Plain Mercedes-only filter for the other Mercedes Signature package
      return (!platePresent || isMercedes ? true : !isMercedesOnly(p));
    });
  const visibleServicePackages = filterForVehicle(servicePackages);

  // Combo helpers
  const starlightOptions = filterForVehicle(packages["Starlights"] || []);
  const ambientOptions = filterForVehicle(packages["Ambient Lights"] || []);

  // Shooting Stars eligibility
  const shootingStarsEligible = (pkgName) =>
    (addonsInfo.shooting_stars?.eligible_packages || []).includes(pkgName);
  const ssPrice = addonsInfo.shooting_stars?.price ?? 80;
  const scPrice = addonsInfo.screen_code?.price ?? 200;

  // Active add-ons given current selection
  const currentStarlightName = service === "Combo" ? starlightPkg : (service === "Starlights" ? pkg : null);
  const currentAmbientName = service === "Combo" ? ambientPkg : (service === "Ambient Lights" ? pkg : null);
  const ssAvailable = !!currentStarlightName && shootingStarsEligible(currentStarlightName);
  // Screen Code never applies to the already-screen-coded Mercedes Signature package
  const scAvailable =
    !!currentAmbientName &&
    currentAmbientName !== "Mercedes Screen Coded Factory Lighting" &&
    eligibleForScreenCode;
  const ssActive = ssAvailable && shootingStars;
  const scActive = scAvailable && screenCode;
  const addonTotal = (ssActive ? ssPrice : 0) + (scActive ? scPrice : 0);
  // Site-wide Eid promo (20% off everything) — applied on top of base + add-ons.
  const promoActive = !!promoInfo?.active;
  const promoPct = promoInfo?.discount_pct ?? 0;
  const applyPromo = (n) => (promoActive ? Math.round(n * (1 - promoPct / 100) * 100) / 100 : n);
  const selectedStarlight = starlightOptions.find((p) => p.name === starlightPkg);
  const selectedAmbient = ambientOptions.find((p) => p.name === ambientPkg);
  const comboSubtotal = (selectedStarlight?.price || 0) + (selectedAmbient?.price || 0);
  const comboDiscountPct = comboInfo?.discount_pct ?? 15;
  const comboAfterCombo = Math.round(comboSubtotal * (1 - comboDiscountPct / 100) * 100) / 100;
  const comboTotal = applyPromo(comboAfterCombo + addonTotal);

  const canNext1 =
    service &&
    vehicle.trim().length >= 2 &&
    (service === "Combo"
      ? !!starlightPkg && !!ambientPkg
      : visibleServicePackages.length === 0 || pkg);
  const canNext2 = bookingDate && timeSlot;
  const canSubmit = name.trim() && email.includes("@") && phone.trim().length >= 5 && agreed;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await api.post("/bookings/checkout", {
        booking: {
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          vehicle: vehicle.trim(),
          reg: reg.trim().toUpperCase().replace(/\s+/g, "") || null,
          service,
          package: service === "Combo" ? null : (pkg || null),
          starlight_package: service === "Combo" ? starlightPkg : null,
          ambient_package: service === "Combo" ? ambientPkg : null,
          screen_code: scActive,
          shooting_stars: ssActive,
          booking_date: format(bookingDate, "yyyy-MM-dd"),
          time_slot: timeSlot,
          notes: notes.trim(),
        },
        origin_url: window.location.origin,
        promo_code: promoActiveApplied ? promoCode.trim().toUpperCase() : null,
      });
      if (res.data?.url) {
        // Redirect to Stripe Checkout
        window.location.href = res.data.url;
        return;
      }
      throw new Error("No checkout URL returned");
    } catch (e) {
      toast.error(formatApiError(e));
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <BookingSuccess
        name={name}
        service={service}
        bookingDate={bookingDate}
        timeSlot={timeSlot}
        email={email}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 pt-32 pb-20">
        <div className="text-xs tracking-[0.35em] text-[#D946EF] mb-3">BOOK AN INSTALL</div>
        <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tighter">Reserve your slot.</h1>
        <p className="text-zinc-400 mt-3 font-body">Three quick steps. We'll handle the rest.</p>

        {promoActive && (
          <div data-testid="eid-banner" className="mt-6 px-5 py-4 rounded-sm border border-[#D946EF]/40 bg-gradient-to-r from-[#D946EF]/10 via-[#0c0c0c] to-[#FF2D87]/10">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#D946EF] text-white text-[10px] font-bold tracking-[0.2em]">EID SPECIAL</span>
              <div className="text-sm text-zinc-200">
                <b className="text-white">{promoPct}% off everything</b> — auto-applied. Ends {promoInfo.ends_on}.
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 px-5 py-3 rounded-sm border border-white/10 bg-[#0a0a0a] text-xs text-zinc-400">
          <b className="text-zinc-200">Heads up:</b> once you book in, you'll receive a confirmation email containing the workshop address &mdash; <span className="text-white">Unit 15, Rainham, RM13 8HZ</span>.
        </div>

        {/* stepper */}
        <div className="flex items-center gap-2 mt-10 mb-10">
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex-1 flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                step >= n ? "bg-[#D946EF] text-white" : "bg-[#0d0d0d] border border-white/10 text-zinc-500"
              }`} data-testid={`step-indicator-${n}`}>
                {step > n ? <Check className="w-4 h-4" /> : n}
              </div>
              {n < 3 && <div className={`flex-1 h-px ${step > n ? "bg-[#D946EF]" : "bg-white/10"}`} />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-7" data-testid="step-1">
            {/* REG-FIRST GATE */}
            {!regConfirmed ? (
              <div data-testid="reg-gate" className="relative rounded-sm border border-[#D946EF]/40 bg-gradient-to-br from-[#D946EF]/10 via-[#0c0c0c] to-[#FF2D87]/10 p-7 sm:p-9">
                <div className="absolute -top-3 left-6 px-2.5 py-0.5 rounded-full bg-[#D946EF] text-white text-[10px] font-bold tracking-[0.25em]">
                  STEP 1 · YOUR CAR
                </div>
                <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tighter">
                  Drop your reg first.<br />
                  We'll show you <span className="neon-cyan">your car's packages.</span>
                </h2>
                <p className="mt-3 text-zinc-400 text-sm font-body max-w-xl leading-relaxed">
                  Type your number plate — we'll auto-match make &amp; model, surface the
                  <b className="text-white"> packages your car supports</b> (Signature Premium for
                  Mercedes, Screen Code for compatible BMW/Audi/VW), and prep the right kit.
                </p>

                <form onSubmit={lookupReg} className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3" data-testid="reg-gate-form">
                  <div className="flex items-stretch rounded-md overflow-hidden shadow-[0_0_30px_rgba(255,213,0,0.25)] border-2 border-black/40">
                    <div className="bg-[#003399] text-white px-3 flex flex-col items-center justify-center min-w-[34px]">
                      <div className="text-yellow-400 text-[10px] font-bold leading-none">★★★</div>
                      <div className="text-yellow-400 text-[10px] font-bold leading-none mt-0.5">GB</div>
                    </div>
                    <input
                      data-testid="reg-gate-input"
                      value={reg}
                      onChange={(e) => setReg(formatReg(e.target.value))}
                      placeholder="YOUR REG"
                      aria-label="Vehicle registration"
                      spellCheck="false"
                      autoComplete="off"
                      autoFocus
                      className="bg-[#FFD500] text-black font-display font-black text-2xl sm:text-3xl tracking-[0.18em] uppercase placeholder:text-black/40 px-5 py-4 w-full sm:w-[260px] outline-none border-0"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={!reg.trim() || regChecking}
                    data-testid="reg-gate-submit"
                    className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#D946EF] text-white font-semibold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#F0ABFC] transition whitespace-nowrap"
                  >
                    {regChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {regChecking ? "Checking…" : "Match my car"}
                    {!regChecking && <ArrowRight className="w-4 h-4" />}
                  </button>
                </form>

                <button
                  type="button"
                  onClick={skipReg}
                  data-testid="reg-gate-skip"
                  className="mt-5 text-xs text-zinc-500 hover:text-zinc-300 underline underline-offset-4 transition"
                >
                  Don't have my reg — skip and pick manually
                </button>
              </div>
            ) : (
              /* Once the reg is captured, show a compact confirmation strip with edit option */
              <div data-testid="reg-confirmed-strip" className="flex items-center justify-between p-4 rounded-sm border border-white/10 bg-[#0a0a0a]">
                <div className="flex items-center gap-3 min-w-0">
                  {!regSkipped ? (
                    <>
                      <div className="flex items-stretch rounded overflow-hidden border border-black/30 shrink-0">
                        <div className="bg-[#003399] text-white px-2 flex flex-col items-center justify-center">
                          <div className="text-yellow-400 text-[8px] font-bold leading-none">★★★</div>
                          <div className="text-yellow-400 text-[8px] font-bold leading-none mt-0.5">GB</div>
                        </div>
                        <span className="bg-[#FFD500] text-black font-display font-black text-sm tracking-[0.18em] uppercase px-3 py-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                          {formatReg(reg)}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <div className="text-[10px] tracking-[0.25em] text-[#D946EF]">YOUR CAR</div>
                        <div className="text-sm text-white truncate">{vehicle || "Vehicle details"}</div>
                      </div>
                    </>
                  ) : (
                    <div>
                      <div className="text-[10px] tracking-[0.25em] text-zinc-500">VEHICLE</div>
                      <div className="text-sm text-zinc-300">Skipped — enter manually below</div>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => { setRegConfirmed(false); setRegSkipped(false); }}
                  data-testid="reg-edit"
                  className="text-xs text-zinc-400 hover:text-[#D946EF] underline underline-offset-4 transition whitespace-nowrap"
                >
                  Change
                </button>
              </div>
            )}

            {/* Service + package picker — only shown once reg is confirmed/skipped */}
            {regConfirmed && (
            <>
            <div>
              <Label className="text-sm text-zinc-300 mb-3 block">Choose a service</Label>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {SERVICES.map((s) => {
                  const isCombo = s === "Combo";
                  return (
                    <button
                      type="button"
                      key={s}
                      data-testid={`service-option-${s.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setService(s)}
                      className={`relative p-5 text-left rounded-sm border transition-all ${
                        service === s
                          ? "border-[#D946EF] bg-[#D946EF]/5 shadow-[0_0_30px_rgba(217,70,239,0.2)]"
                          : isCombo
                          ? "border-[#FF2D87]/40 bg-gradient-to-br from-[#D946EF]/5 to-[#FF2D87]/5 hover:border-[#FF2D87]"
                          : "border-white/10 bg-[#0d0d0d] hover:border-white/30"
                      }`}
                    >
                      {isCombo && (
                        <div className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full bg-[#FF2D87] text-white text-[10px] font-bold tracking-wider">
                          -{comboDiscountPct}%
                        </div>
                      )}
                      <div className="font-display font-semibold text-lg">{s}</div>
                      <div className="text-xs text-zinc-500 mt-1">
                        {isCombo ? "Starlights + Ambient · save" : "Tap to select"}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {service === "Combo" && (
              <div data-testid="combo-picker" className="space-y-6">
                <div className="p-4 rounded-sm border border-[#FF2D87]/30 bg-gradient-to-br from-[#D946EF]/5 to-[#FF2D87]/5 text-sm text-zinc-300">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 rounded-full bg-[#FF2D87] text-white text-[10px] font-bold tracking-wider">
                      SAVE {comboDiscountPct}%
                    </span>
                    <span className="text-white font-semibold">Combo bundle</span>
                  </div>
                  <p className="text-zinc-400 leading-relaxed">
                    Pick one Starlight package and one Ambient package — we'll discount the combined
                    total by {comboDiscountPct}% at checkout. Same install day. Same OEM finish.
                  </p>
                </div>

                <div data-testid="combo-starlight-picker">
                  <Label className="text-sm text-zinc-300 mb-3 block">1 · Pick your Starlight package</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {starlightOptions.map((p) => {
                      const selected = p.name === starlightPkg;
                      return (
                        <button
                          type="button"
                          key={p.name}
                          data-testid={`combo-starlight-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                          onClick={() => setStarlightPkg(p.name)}
                          className={`relative text-left p-4 rounded-sm border transition-all ${
                            selected
                              ? "border-[#D946EF] bg-[#D946EF]/5 shadow-[0_0_30px_rgba(217,70,239,0.2)]"
                              : "border-white/10 bg-[#0d0d0d] hover:border-white/30 hover:-translate-y-0.5"
                          }`}
                        >
                          <div className="font-display font-bold text-base tracking-tight">{p.name}</div>
                          <div className="mt-1 font-display font-black text-xl text-[#D946EF]">£{p.price.toFixed(0)}</div>
                          {selected && (
                            <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#D946EF] flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" strokeWidth={3} />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div data-testid="combo-ambient-picker">
                  <Label className="text-sm text-zinc-300 mb-3 block">2 · Pick your Ambient package</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {ambientOptions.map((p) => {
                      const selected = p.name === ambientPkg;
                      return (
                        <button
                          type="button"
                          key={p.name}
                          data-testid={`combo-ambient-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                          onClick={() => setAmbientPkg(p.name)}
                          className={`relative text-left p-4 rounded-sm border transition-all ${
                            selected
                              ? "border-[#FF2D87] bg-[#FF2D87]/5 shadow-[0_0_30px_rgba(255,45,135,0.2)]"
                              : "border-white/10 bg-[#0d0d0d] hover:border-white/30 hover:-translate-y-0.5"
                          }`}
                        >
                          {p.tier && (
                            <div className="text-[10px] tracking-[0.25em] text-zinc-500 mb-1">{p.tier.toUpperCase()}</div>
                          )}
                          <div className="font-display font-bold text-base tracking-tight">{p.name}</div>
                          <div className="mt-1 font-display font-black text-xl text-[#FF2D87]">£{p.price.toFixed(0)}</div>
                          {selected && (
                            <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#FF2D87] flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" strokeWidth={3} />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {selectedStarlight && selectedAmbient && (
                  <>
                    {/* Combo add-ons */}
                    {(ssAvailable || scAvailable) && (
                      <div data-testid="combo-addons-block" className="space-y-3">
                        <div className="text-[10px] tracking-[0.3em] text-zinc-500">ADD-ONS</div>
                        {ssAvailable && (
                          <label htmlFor="combo-addon-ss" className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition select-none ${shootingStars ? "border-[#D946EF]/60 bg-[#D946EF]/5" : "border-white/10 bg-[#0a0a0a] hover:border-white/30"}`}>
                            <input id="combo-addon-ss" type="checkbox" checked={shootingStars} onChange={(e) => setShootingStars(e.target.checked)} data-testid="combo-addon-shooting-stars" className="w-4 h-4 accent-[#D946EF] cursor-pointer" />
                            <div className="flex-1 flex items-center justify-between">
                              <div><b className="text-white">Shooting Stars</b> <span className="text-xs text-zinc-400">— animation upgrade</span></div>
                              <span className="font-display font-bold text-[#D946EF]">+£{ssPrice}</span>
                            </div>
                          </label>
                        )}
                        {scAvailable && (
                          <div data-testid="combo-control-method-picker" className="grid grid-cols-2 gap-2">
                            <button type="button" data-testid="combo-control-app" onClick={() => setScreenCode(false)} className={`text-left p-3 rounded-sm border transition ${!screenCode ? "border-[#D946EF] bg-[#D946EF]/10" : "border-white/10 bg-[#0d0d0d] hover:border-white/30"}`}>
                              <div className="flex items-center justify-between">
                                <span className="font-display font-semibold text-sm">App Controlled</span>
                                <span className="text-[10px] tracking-[0.2em] text-zinc-500">INCLUDED</span>
                              </div>
                              <div className="text-xs text-zinc-400 mt-1">Phone app.</div>
                            </button>
                            <button type="button" data-testid="combo-control-screen" onClick={() => setScreenCode(true)} className={`text-left p-3 rounded-sm border transition ${screenCode ? "border-[#D946EF] bg-[#D946EF]/10" : "border-white/10 bg-[#0d0d0d] hover:border-white/30"}`}>
                              <div className="flex items-center justify-between">
                                <span className="font-display font-semibold text-sm">Screen Coded</span>
                                <span className="text-[10px] font-bold text-[#D946EF]">+£{scPrice}</span>
                              </div>
                              <div className="text-xs text-zinc-400 mt-1">OEM screen control.</div>
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  <div data-testid="combo-summary" className="p-5 rounded-sm border border-[#D946EF]/40 bg-[#0d0d0d]">
                    <div className="text-[10px] tracking-[0.3em] text-[#D946EF] mb-3">COMBO TOTAL</div>
                    <div className="grid grid-cols-2 gap-y-1.5 text-sm">
                      <div className="text-zinc-500">{selectedStarlight.name}</div>
                      <div className="text-right text-zinc-300">£{selectedStarlight.price.toFixed(0)}</div>
                      <div className="text-zinc-500">{selectedAmbient.name}</div>
                      <div className="text-right text-zinc-300">£{selectedAmbient.price.toFixed(0)}</div>
                      <div className="text-zinc-500 pt-2 border-t border-white/10">Subtotal</div>
                      <div className="text-right text-zinc-300 pt-2 border-t border-white/10">£{comboSubtotal.toFixed(0)}</div>
                      <div className="text-[#34d399]">Combo discount</div>
                      <div className="text-right text-[#34d399]">-{comboDiscountPct}%</div>
                      {ssActive && (<><div className="text-zinc-500">Shooting Stars</div><div className="text-right text-zinc-300">+£{ssPrice}</div></>)}
                      {scActive && (<><div className="text-zinc-500">Screen Code</div><div className="text-right text-zinc-300">+£{scPrice}</div></>)}
                      <div className="text-white font-semibold pt-2 border-t border-white/10">Total</div>
                      <div className="text-right font-display font-black text-2xl text-[#D946EF] pt-2 border-t border-white/10">£{comboTotal.toFixed(0)}</div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-white/10 text-xs text-zinc-500">
                      You'll pay a £50 deposit now; balance £{Math.max(0, comboTotal - 50).toFixed(0)} on install day.
                    </div>
                  </div>
                  </>
                )}
              </div>
            )}

            {service && service !== "Combo" && visibleServicePackages.length > 0 && (
              <div data-testid="package-picker">
                <Label className="text-sm text-zinc-300 mb-3 block">Pick your {service} package</Label>
                <div className={`grid gap-3 ${service === "Starlights" ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-2"}`}>
                  {visibleServicePackages.map((p) => {
                    const selected = p.name === pkg;
                    return (
                      <button
                        type="button"
                        key={p.name}
                        data-testid={`package-option-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => setPkg(p.name)}
                        className={`relative overflow-hidden text-left rounded-sm border transition-all ${
                          selected
                            ? "border-[#D946EF] bg-[#D946EF]/5 shadow-[0_0_30px_rgba(217,70,239,0.2)]"
                            : "border-white/10 bg-[#0d0d0d] hover:border-white/30 hover:-translate-y-0.5"
                        }`}
                      >
                        {p.image && (
                          <div className="relative w-full aspect-[16/10] overflow-hidden border-b border-white/5">
                            <img src={p.image} alt={`${p.name} install example`} className="w-full h-full object-cover" loading="lazy" />
                            <div className="absolute bottom-0 inset-x-0 h-12 bg-gradient-to-t from-[#0d0d0d] to-transparent" />
                          </div>
                        )}
                        <div className="p-5">
                          {p.tier && (
                            <div className="text-[10px] tracking-[0.25em] text-zinc-500 mb-1">{p.tier.toUpperCase()}</div>
                          )}
                          <div className="font-display font-bold text-lg tracking-tight">{p.name}</div>
                          <div className="mt-1 font-display font-black text-2xl text-[#D946EF]">£{p.price.toFixed(0)}</div>
                          {p.features && p.features.length > 0 && (
                            <ul className="mt-3 space-y-1.5">
                              {p.features.map((f) => (
                                <li key={f} className="flex items-start gap-2 text-xs text-zinc-400">
                                  <span className="mt-1 w-1 h-1 rounded-full bg-[#D946EF] shrink-0" />
                                  <span>{f}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        {selected && (
                          <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-[#D946EF] flex items-center justify-center">
                            <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Add-ons: Shooting Stars (Starlights) / Screen Code (Ambient) */}
                {(ssAvailable || scAvailable) && (
                  <div data-testid="addons-block" className="mt-5 space-y-3">
                    <div className="text-[10px] tracking-[0.3em] text-zinc-500">ADD-ONS</div>
                    {ssAvailable && (
                      <label
                        htmlFor="addon-shooting-stars"
                        data-testid="addon-shooting-stars-row"
                        className={`flex items-start gap-3 p-4 rounded-sm border cursor-pointer transition select-none ${
                          shootingStars
                            ? "border-[#D946EF]/60 bg-[#D946EF]/5"
                            : "border-white/10 bg-[#0a0a0a] hover:border-white/30"
                        }`}
                      >
                        <input
                          id="addon-shooting-stars"
                          type="checkbox"
                          checked={shootingStars}
                          onChange={(e) => setShootingStars(e.target.checked)}
                          data-testid="addon-shooting-stars-checkbox"
                          className="mt-1 w-4 h-4 accent-[#D946EF] cursor-pointer shrink-0"
                        />
                        <div className="flex-1 flex items-start justify-between gap-3">
                          <div>
                            <div className="font-display font-semibold text-base text-white">Shooting Stars</div>
                            <div className="text-xs text-zinc-400 mt-0.5">Add shooting-star animation to your headliner.</div>
                          </div>
                          <div className="font-display font-bold text-[#D946EF] whitespace-nowrap">+£{ssPrice}</div>
                        </div>
                      </label>
                    )}
                    {scAvailable && (
                      <div data-testid="control-method-picker" className="p-4 rounded-sm border border-white/10 bg-[#0a0a0a]">
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div>
                            <div className="font-display font-semibold text-base text-white">Control method</div>
                            <div className="text-xs text-zinc-400 mt-0.5">Choose how you'd like to control the ambient lights.</div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            data-testid="control-app"
                            onClick={() => setScreenCode(false)}
                            className={`text-left p-3 rounded-sm border transition ${
                              !screenCode
                                ? "border-[#D946EF] bg-[#D946EF]/10"
                                : "border-white/10 bg-[#0d0d0d] hover:border-white/30"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="font-display font-semibold text-sm text-white">App Controlled</div>
                              <div className="text-[10px] tracking-[0.2em] text-zinc-500">INCLUDED</div>
                            </div>
                            <div className="text-xs text-zinc-400 mt-1">Colour &amp; brightness via phone app.</div>
                          </button>
                          <button
                            type="button"
                            data-testid="control-screen"
                            onClick={() => setScreenCode(true)}
                            className={`text-left p-3 rounded-sm border transition ${
                              screenCode
                                ? "border-[#D946EF] bg-[#D946EF]/10"
                                : "border-white/10 bg-[#0d0d0d] hover:border-white/30"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="font-display font-semibold text-sm text-white">Screen Coded</div>
                              <div className="text-[10px] font-bold text-[#D946EF]">+£{scPrice}</div>
                            </div>
                            <div className="text-xs text-zinc-400 mt-1">Controlled via your car's OEM screen — no app needed.</div>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Combo upsell — appears once a Starlight or Ambient package is selected */}
                {selectedPkg && (service === "Starlights" || service === "Ambient Lights") && (() => {
                  const otherService = service === "Starlights" ? "Ambient Lights" : "Starlights";
                  const otherOptions = packages[otherService] || [];
                  const cheapestOther = otherOptions.reduce(
                    (min, p) => (min === null || p.price < min.price ? p : min),
                    null,
                  );
                  if (!cheapestOther) return null;
                  const savings = Math.round((selectedPkg.price + cheapestOther.price) * (comboDiscountPct / 100));
                  return (
                    <button
                      type="button"
                      data-testid="combo-upsell-nudge"
                      onClick={() => {
                        if (service === "Starlights") {
                          setStarlightPkg(selectedPkg.name);
                        } else {
                          setAmbientPkg(selectedPkg.name);
                        }
                        setService("Combo");
                      }}
                      className="group mt-4 w-full text-left relative overflow-hidden rounded-sm border border-[#FF2D87]/30 bg-gradient-to-r from-[#D946EF]/10 via-[#0c0c0c] to-[#FF2D87]/10 p-5 hover:border-[#FF2D87] transition-all"
                    >
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-3">
                          <span className="px-2 py-0.5 rounded-full bg-[#FF2D87] text-white text-[10px] font-bold tracking-wider">
                            SAVE {comboDiscountPct}%
                          </span>
                          <div>
                            <div className="font-display font-semibold text-base text-white">
                              Add {otherService} and save £{savings}
                            </div>
                            <div className="text-xs text-zinc-400 mt-0.5">
                              Bundle both services on one booking — same install day, OEM finish.
                            </div>
                          </div>
                        </div>
                        <div className="inline-flex items-center gap-2 text-sm font-semibold text-[#FF2D87] group-hover:text-[#ff5fa0] transition">
                          Switch to Combo <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                        </div>
                      </div>
                    </button>
                  );
                })()}
              </div>
            )}

            {service === "Custom Parts" ? (
              <CustomPartsPanel />
            ) : (
              <>
                <div>
                  <Label className="text-sm text-zinc-300 mb-3 block" htmlFor="vehicle">Your vehicle</Label>
                  {initialReg && (
                    <div data-testid="reg-banner" className="mb-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFD500]/10 border border-[#FFD500]/30 text-xs">
                      <span className="font-display font-bold text-[#FFD500] tracking-wider">REG · {formatReg(initialReg)}</span>
                      <span className="text-zinc-400">captured — add make/model below</span>
                    </div>
                  )}
                  <Input
                    id="vehicle"
                    data-testid="vehicle-input"
                    value={vehicle}
                    onChange={(e) => setVehicle(e.target.value)}
                    placeholder="e.g. 2022 BMW M3 Competition"
                    className={inputCls}
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    disabled={!canNext1}
                    onClick={() => setStep(2)}
                    data-testid="next-btn-1"
                    className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#D946EF] text-white font-semibold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#F0ABFC] transition"
                  >
                    Continue <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}
            </>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-7" data-testid="step-2">
            <div>
              <Label className="text-sm text-zinc-300 mb-3 block">Select a date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    data-testid="date-picker-btn"
                    className={`w-full ${inputCls} flex items-center px-4 text-left ${bookingDate ? "text-white" : "text-zinc-500"}`}
                  >
                    <CalendarIcon className="w-4 h-4 mr-3 text-[#D946EF]" />
                    {bookingDate ? format(bookingDate, "EEEE d MMMM yyyy") : "Choose a date…"}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="bg-[#0d0d0d] border-white/10 p-2" align="start">
                  <Calendar
                    mode="single"
                    selected={bookingDate}
                    onSelect={(d) => { setBookingDate(d); setTimeSlot(""); }}
                    disabled={(d) => {
                      const today = new Date(); today.setHours(0, 0, 0, 0);
                      return d < today || d.getDay() === 0; // Sundays closed
                    }}
                    className="text-white"
                  />
                </PopoverContent>
              </Popover>
            </div>

            {bookingDate && (
              <div>
                <Label className="text-sm text-zinc-300 mb-3 block">Pick a time block</Label>
                {restrictedDay && (
                  <div data-testid="restricted-day-banner" className="mb-4 p-3 rounded-sm border border-yellow-500/30 bg-yellow-500/5 text-xs text-zinc-300">
                    <b className="text-yellow-300">Limited capacity until 24 May:</b> we're taking 1 booking per 3-hour block on this date.
                    {(service === "Starlights" || service === "Combo") && (
                      <>
                        {" "}
                        Only <b className="text-white">1 Starlight install per day</b> available — {starlightRemaining === 0 ? "this date is already booked, please pick another." : "secure yours below."}
                      </>
                    )}
                  </div>
                )}
                {loadingSlots ? (
                  <div className="flex items-center gap-2 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Checking availability…</div>
                ) : (
                  <div className="grid sm:grid-cols-3 gap-3" data-testid="time-blocks-grid">
                    {BLOCKS.map((b) => {
                      const remaining = blocksRemaining[b.api_label];
                      const remainingDisplay = remaining === undefined ? maxPerBlock : remaining;
                      const passed = !!blocksPassed[b.api_label];
                      // Block-level full
                      const blockFull = remainingDisplay === 0 && !passed;
                      // Starlight-per-day cap blocks ALL slots when service is Starlights/Combo
                      const starlightBlocked =
                        restrictedDay &&
                        (service === "Starlights" || service === "Combo") &&
                        starlightRemaining === 0;
                      const full = passed || blockFull || starlightBlocked;
                      const selected = b.start === timeSlot;
                      return (
                        <button
                          key={b.id}
                          type="button"
                          disabled={full}
                          onClick={() => setTimeSlot(b.start)}
                          data-testid={`time-block-${b.id}`}
                          className={`relative text-left p-5 rounded-sm border transition-all ${
                            full
                              ? "bg-[#0a0a0a] border-red-500/20 text-zinc-600 cursor-not-allowed"
                              : selected
                              ? "bg-[#D946EF]/10 border-[#D946EF] shadow-[0_0_30px_rgba(217,70,239,0.25)]"
                              : "bg-[#0d0d0d] border-white/10 hover:border-[#D946EF]/60 hover:-translate-y-0.5"
                          }`}
                        >
                          <div className="text-[10px] tracking-[0.25em] mb-2" style={{ color: full ? "#71717a" : (selected ? "#F0ABFC" : "#71717a") }}>
                            {b.label.toUpperCase()}
                          </div>
                          <div className={`font-display font-bold text-2xl tracking-tight ${full ? "line-through" : "text-white"}`}>
                            {b.range}
                          </div>
                          <div className={`mt-3 text-xs tracking-wider font-semibold ${
                            full ? "text-red-400" : remainingDisplay === 1 ? "text-yellow-300" : "text-[#F0ABFC]"
                          }`}>
                            {passed
                              ? "TIME PASSED"
                              : starlightBlocked && !blockFull
                              ? "STARLIGHT TAKEN TODAY"
                              : blockFull
                              ? "FULLY BOOKED"
                              : `${remainingDisplay} of ${maxPerBlock} ${maxPerBlock === 1 ? "spot" : "spots"} left`}
                          </div>
                          {selected && !full && (
                            <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-[#D946EF] flex items-center justify-center">
                              <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="mt-3 text-xs text-zinc-500 font-body">
                  Each block fits up to {maxPerBlock} {maxPerBlock === 1 ? "booking" : "bookings"}. We'll confirm your exact start time after you book.
                </p>
              </div>
            )}

            <div className="flex justify-between">
              <button onClick={() => setStep(1)} className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition" data-testid="back-btn-2">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                disabled={!canNext2}
                onClick={() => setStep(3)}
                data-testid="next-btn-2"
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#D946EF] text-white font-semibold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#F0ABFC] transition"
              >
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <StepContact
            ctx={{
              name, setName,
              email, setEmail,
              phone, setPhone,
              notes, setNotes,
              agreed, setAgreed,
              service, vehicle, bookingDate, timeSlot,
              selectedPkg, selectedStarlight, selectedAmbient,
              comboSubtotal, comboDiscountPct, comboTotal,
              ssActive, scActive, ssPrice, scPrice,
              promoActive, promoPct, addonTotal, applyPromo,
              submitting, canSubmit, submit, setStep,
              promoCode, setPromoCode, promoState, validatePromo, clearPromo,
              applyCustomerPromo, promoActiveApplied,
            }}
          />
        )}
      </div>
      <Footer />
    </div>
  );
}
